import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createDraft } from '../services/api';

const TEAM_COUNTS = [8, 10, 12, 14];
// Sensible v1 default -- shown collapsed behind a "Customize Positions" toggle so the common case
// (accept the default) stays a one-short-screen flow, matching DraftSetup.jsx's own defaults
// (Commissioner Mode setup still skips full scoring customization; that can be added later).
const DEFAULT_ROSTER = { QB: 1, RB: 2, WR: 2, TE: 1, FLEX: 1, K: 1, DEF: 1, BN: 6 };
const ROSTER_POSITIONS = Object.keys(DEFAULT_ROSTER);

/**
 * Standalone room-creation form for Commissioner Mode -- deliberately not wired through
 * DraftContext/DraftSetup.jsx (those assume the creator immediately becomes the sole drafter and
 * jump straight to the board; Commissioner Mode creates a LOBBY-status draft that needs a waiting
 * room first). Calls the API directly and lands on the new lobby.
 */
function DraftCommissionerSetupView() {
  const navigate = useNavigate();
  const [draftName, setDraftName] = useState('');
  const [teamCount, setTeamCount] = useState(10);
  const [rounds, setRounds] = useState(15);
  const [pickTimerSeconds, setPickTimerSeconds] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [rosterExpanded, setRosterExpanded] = useState(false);
  const [rosterSlots, setRosterSlots] = useState({ ...DEFAULT_ROSTER });

  function handleSlotChange(pos, delta) {
    setRosterSlots(prev => ({
      ...prev,
      [pos]: Math.max(0, Math.min(10, prev[pos] + delta)),
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setCreating(true);
    setError(null);
    try {
      const draft = await createDraft({
        draftName: draftName.trim() || 'My Draft',
        teamCount,
        rounds: Number(rounds),
        draftType: 'SNAKE',
        userTeamPosition: 1,
        sport: 'nfl',
        rosterConfig: rosterSlots,
        scoringFormat: 'half_ppr',
        commissionerMode: true,
        pickTimerSeconds: pickTimerSeconds ? Number(pickTimerSeconds) : null,
      });
      navigate(`/draft/${draft.draftId}/lobby`);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to create draft room');
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className='card' style={{ maxWidth: 480, margin: '0 auto' }}>
      <h2>Create a Commissioner Draft</h2>
      <p style={{ color: 'var(--text-muted)', marginBottom: 16 }}>
        Set up a live draft room and invite friends with a shareable code.
      </p>
      <form onSubmit={handleSubmit}>
        <div className='form-group'>
          <label>Draft Name</label>
          <input
            className='input'
            style={{ width: '100%' }}
            placeholder='My Draft'
            value={draftName}
            onChange={e => setDraftName(e.target.value)}
          />
        </div>

        <div className='form-group'>
          <label>Number of Teams</label>
          <div className='filter-group'>
            {TEAM_COUNTS.map(n => (
              <button
                type='button'
                key={n}
                className={`filter-pill ${teamCount === n ? 'active' : ''}`}
                onClick={() => setTeamCount(n)}
              >
                {n}
              </button>
            ))}
          </div>
        </div>

        <div className='form-group'>
          <label htmlFor='rounds'>Rounds</label>
          <input
            id='rounds'
            className='input'
            type='number'
            min={1}
            max={30}
            value={rounds}
            onChange={e => setRounds(e.target.value)}
          />
        </div>

        <div className='form-group'>
          <label>Roster Positions</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
              {ROSTER_POSITIONS.map(pos => `${pos} ${rosterSlots[pos]}`).join(' · ')}
            </span>
            <button
              type='button'
              className='btn btn-secondary'
              style={{ marginLeft: 'auto' }}
              onClick={() => setRosterExpanded(e => !e)}
            >
              {rosterExpanded ? 'Hide' : 'Customize Positions'}
            </button>
          </div>
          {rosterExpanded && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '8px 16px', marginTop: 8 }}>
              {ROSTER_POSITIONS.map(pos => (
                <div key={pos} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 36, fontSize: '0.85rem', fontWeight: 500 }}>{pos}</span>
                  <button
                    type='button'
                    className='filter-pill'
                    style={{ padding: '2px 10px', minWidth: 28 }}
                    onClick={() => handleSlotChange(pos, -1)}
                    disabled={rosterSlots[pos] === 0}
                    aria-label={`Decrease ${pos} slots`}
                  >
                    −
                  </button>
                  <span style={{ width: 20, textAlign: 'center' }}>{rosterSlots[pos]}</span>
                  <button
                    type='button'
                    className='filter-pill'
                    style={{ padding: '2px 10px', minWidth: 28 }}
                    onClick={() => handleSlotChange(pos, 1)}
                    disabled={rosterSlots[pos] === 10}
                    aria-label={`Increase ${pos} slots`}
                  >
                    +
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className='form-group'>
          <label htmlFor='pick-timer'>Pick Timer (seconds, optional)</label>
          <input
            id='pick-timer'
            className='input'
            type='number'
            min={0}
            placeholder='No timer'
            value={pickTimerSeconds}
            onChange={e => setPickTimerSeconds(e.target.value)}
          />
        </div>

        {error && <div className='draft-join-error'>{error}</div>}

        <button type='submit' className='btn btn-primary' disabled={creating}>
          {creating ? 'Creating…' : 'Create Draft Room'}
        </button>
      </form>
    </div>
  );
}

export default DraftCommissionerSetupView;
